/**
 * researchJobsStore — os trabalhos de busca do plano do fabricante que correm
 * em SEGUNDO PLANO, chaveados por `jobId`.
 *
 * Espelha `importJobStore` de propósito: chaveado por id, um `apply*` por
 * evento, componente assina o store direto (sem passar propriedade a
 * propriedade). É o molde do repositório para trabalho assíncrono e ele já
 * provou que funciona com a importação de planilha.
 *
 * POR QUE `jobId` E NÃO `equipmentId` (contrato §2.2): a chave do acervo é
 * determinística por fabricante+modelo, então dois inquilinos buscando o mesmo
 * GA37 recebem a MESMA chave. O identificador do trabalho é nosso, um por
 * (inquilino, conversa, equipamento, submissão) — é isso que impede dois
 * trabalhos simultâneos de se misturarem.
 *
 * A GUARDA DE INQUILINO NÃO MORA AQUI: mora no despachante
 * (`connectionStore`), antes de chamar qualquer `apply*`, igual a todos os
 * outros avisos. Este store nunca vê frame de outro inquilino.
 *
 * O ESTADO AQUI É CONVENIÊNCIA, NÃO A VERDADE (contrato §2.5). A verdade é a
 * mensagem persistida na conversa: o plano chega mesmo que o aviso na tela
 * falhe. Este store existe para o usuário saber AGORA e para o aviso não
 * reaparecer duplicado.
 */
import { create } from 'zustand'
import { api } from '../lib/api'
import type {
  PlanResearchProgressEvent,
  PlanResearchReadyEvent,
  PlanResearchFailedEvent,
  PlanResearchFailureReason,
  ResearchJobActiveRow,
  ResearchJobsActiveResponse,
} from '../types/research-jobs'
import { isManualIngestRow, type ManualIngestActiveRow } from '../types/research-jobs'
import { useManualIngestJobsStore } from './manualIngestJobsStore'

export type ResearchJobStatus = 'running' | 'ready' | 'failed'

export interface ResearchJobSnapshot {
  jobId: string
  equipmentId: string
  conversationId: string
  label: string
  /** Época em ms — o contador do aviso é `agora - startedAt`, calculado na tela. */
  startedAt: number
  ceilingMs: number
  status: ResearchJobStatus
  researchId: string | null
  /** Último andamento declarado pelo motor (o contador local não depende dele). */
  elapsedMs: number
  /**
   * true = o usuário já confirmou o plano provisório, então o plano do
   * fabricante NÃO pode trocar o card no lugar: tem de vir card de comparação.
   */
  confirmedByUser: boolean
  /** true quando o pouso precisa de comparação (o usuário confirmou, ou o motor declarou conflito). */
  needsComparison: boolean
  reason?: PlanResearchFailureReason
  /** Quando o trabalho chegou a um estado final — o aviso some 10s depois disso. */
  settledAt?: number
}

interface ResearchJobsStore {
  jobs: Record<string, ResearchJobSnapshot>
  applyProgress: (event: PlanResearchProgressEvent) => void
  applyReady: (event: PlanResearchReadyEvent) => void
  applyFailed: (event: PlanResearchFailedEvent) => void
  /** O usuário confirmou o plano provisório deste trabalho. */
  markConfirmedByUser: (jobId: string) => void
  /** Reergue os trabalhos em voo no carregamento e na reconexão. */
  hydrate: () => Promise<void>
  getJob: (jobId: string) => ResearchJobSnapshot | undefined
  clear: (jobId: string) => void
}

function isTerminal(status: ResearchJobStatus): boolean {
  return status === 'ready' || status === 'failed'
}

/**
 * Os redutores ficam FORA do `create` de propósito: cada um é uma função pura
 * `(jobs, evento) -> jobs`, testável sozinha, e o corpo do store não vira um
 * bloco de 140 linhas que o portão de modularidade reprova.
 */
type Jobs = Record<string, ResearchJobSnapshot>

function reduceProgress(jobs: Jobs, event: PlanResearchProgressEvent): Jobs {
  const existing = jobs[event.jobId]
  // Andamento nunca ressuscita trabalho que já terminou: o `ready` pode chegar
  // antes de um `progress` atrasado que ainda estava no fio.
  if (existing && isTerminal(existing.status)) return jobs
  const snapshot: ResearchJobSnapshot = {
    ...(existing ?? {
      jobId: event.jobId,
      researchId: null,
      confirmedByUser: false,
      needsComparison: false,
    }),
    jobId: event.jobId,
    equipmentId: event.equipmentId,
    conversationId: event.conversationId,
    label: event.label,
    ceilingMs: event.ceilingMs,
    elapsedMs: event.elapsedMs,
    status: 'running',
    // O relógio do aviso é LOCAL: o motor manda `elapsedMs` no máximo a cada
    // 15s, e um contador que só anda de 15 em 15 parece travado.
    startedAt: Date.now() - event.elapsedMs,
  }
  return { ...jobs, [event.jobId]: snapshot }
}

function reduceReady(jobs: Jobs, event: PlanResearchReadyEvent): Jobs {
  const existing = jobs[event.jobId]
  const confirmedByUser = existing?.confirmedByUser ?? false
  const snapshot: ResearchJobSnapshot = {
    ...(existing ?? {
      jobId: event.jobId,
      startedAt: Date.now(),
      ceilingMs: 300_000,
      elapsedMs: 0,
    }),
    jobId: event.jobId,
    equipmentId: event.equipmentId,
    conversationId: event.conversationId,
    label: event.label,
    status: 'ready',
    researchId: event.researchId,
    confirmedByUser,
    // T9 — o plano do fabricante NUNCA sobrescreve calado o que o usuário já
    // confirmou. Ou o motor declara o conflito, ou a tela sabe que o usuário
    // confirmou; qualquer um dos dois manda COMPARAR em vez de trocar.
    needsComparison: confirmedByUser || event.conflict === true,
    settledAt: Date.now(),
  }
  return { ...jobs, [event.jobId]: snapshot }
}

function reduceFailed(jobs: Jobs, event: PlanResearchFailedEvent): Jobs {
  const existing = jobs[event.jobId]
  const snapshot: ResearchJobSnapshot = {
    ...(existing ?? {
      jobId: event.jobId,
      startedAt: Date.now(),
      ceilingMs: 300_000,
      elapsedMs: 0,
      researchId: null,
      confirmedByUser: false,
      needsComparison: false,
    }),
    jobId: event.jobId,
    equipmentId: event.equipmentId,
    conversationId: event.conversationId,
    label: event.label,
    status: 'failed',
    reason: event.reason,
    settledAt: Date.now(),
  }
  return { ...jobs, [event.jobId]: snapshot }
}

function reduceHydrate(jobs: Jobs, rows: ResearchJobActiveRow[]): Jobs {
  const next = { ...jobs }
  for (const row of rows) {
    const existing = next[row.jobId]
    // O aviso já recebido GANHA do REST: o estado local pode estar à frente (o
    // `ready` chegou pelo socket enquanto a lista ainda estava em voo).
    if (existing && isTerminal(existing.status)) continue
    next[row.jobId] = {
      jobId: row.jobId,
      equipmentId: row.equipmentId,
      conversationId: row.conversationId,
      label: row.label,
      startedAt: Date.parse(row.startedAt),
      ceilingMs: row.ceilingMs,
      status: row.status,
      researchId: row.researchId,
      confirmedByUser: existing?.confirmedByUser ?? false,
      needsComparison: existing?.needsComparison ?? false,
      elapsedMs: existing?.elapsedMs ?? 0,
      ...(isTerminal(row.status) ? { settledAt: Date.now() } : {}),
    }
  }
  return next
}

export const useResearchJobsStore = create<ResearchJobsStore>((set, get) => ({
  jobs: {},

  applyProgress: (event) => set((state) => ({ jobs: reduceProgress(state.jobs, event) })),

  applyReady: (event) => set((state) => ({ jobs: reduceReady(state.jobs, event) })),

  applyFailed: (event) => set((state) => ({ jobs: reduceFailed(state.jobs, event) })),

  markConfirmedByUser: (jobId) => {
    set((state) => {
      const existing = state.jobs[jobId]
      if (!existing) return state
      return { jobs: { ...state.jobs, [jobId]: { ...existing, confirmedByUser: true } } }
    })
  },

  hydrate: async () => {
    let payload: ResearchJobsActiveResponse
    try {
      payload = await api.get<ResearchJobsActiveResponse>('/api/v1/research/jobs/active')
    } catch (err) {
      // Best-effort de propósito: a VERDADE é a mensagem persistida na conversa.
      // Falhar a hidratação não pode derrubar a tela nem apagar o que já existe.
      console.warn('[researchJobs] hydrate falhou', err)
      return
    }
    const all = Array.isArray(payload?.jobs) ? payload.jobs : []
    // O manual grande vem na mesma lista, mas NÃO é pesquisa na web: vai para
    // o próprio store, senão ganharia o aviso e o painel de "buscando plano".
    const rows: ResearchJobActiveRow[] = []
    const manuais: ManualIngestActiveRow[] = []
    for (const row of all) {
      if (isManualIngestRow(row)) manuais.push(row)
      else rows.push(row)
    }
    useManualIngestJobsStore.getState().replaceFromServer(manuais, {
      canDelete: payload?.canDeleteManual === true,
      deletions: Array.isArray(payload?.manualDeletions) ? payload.manualDeletions : [],
    })
    set((state) => ({ jobs: reduceHydrate(state.jobs, rows) }))
  },

  getJob: (jobId) => get().jobs[jobId],

  clear: (jobId) => {
    set((state) => {
      if (!(jobId in state.jobs)) return state
      const next = { ...state.jobs }
      delete next[jobId]
      return { jobs: next }
    })
  },
}))
